import { observer } from "mobx-react-lite";
import "../../style/Play_Game.css";
import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAlbumStore, useGameStore } from "../../stores/useStore";
import DraggableZone from "../../components/game/DraggableZone";
import DroppableZone from "../../components/game/DroppableZone";
import AlbumDetail from "../../components/album/AlbumDetail";
import Timer from "../../components/game/Timer";
import Score from "../../components/game/Score";
import LifeCounter from "../../components/game/LifeCounter";
import AlbumAudioPlayerSmall from "../../components/album/AlbumAudioPlayerSmall"

import useAudioPlayer from "../../hooks/useAudioPlayer.js"

import { Lightbulb } from "lucide-react";

import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";

const GROUPMODE = true;
const ALLMODE = false;


const Game = observer(() => {
  // LOCATION INFO
  const location = useLocation();
  const navigate = useNavigate();
  const {
    gameMode,
    nbAlbumMax,
    filterAlbumMode,
    groupFilterId,
    infiniteMode,
    nbHeart,
    multiplicator,
  } = location.state || {};
  // STORE IMPORT
  const albumStore = useAlbumStore();
  const gameStore = useGameStore();
  const audioObject = useAudioPlayer({ autoPlay: true, loop: false });

  // MISE EN PLACE DU DRAG AND AND DROP
  const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
  const backend = isTouchDevice ? TouchBackend : HTML5Backend;

  const [showHint, setShowHint] = useState(false);
  const [hintActivated, setHintActivated] = useState(false);
  const [titleMusic, setTitleMusic] = useState("");
  const hintActivationTimerRef = useRef(null);

  const endSequence = ({ reason = null }) => {
    if (audioObject) {
      audioObject.pause();
      audioObject.setSrc("");
      audioObject.destroy();
      audioObject.setVolume(0);
    }
    navigate("/Play/Result", {
      state: {
        reason: reason,
      },
    });
  };


  useEffect(() => {
    if (gameStore.isFinished || gameStore.life <= 0) {
      audioObject.pause();
      audioObject.setSrc("");
    }
  }, [gameStore.isFinished, gameStore.life]);


  // USE EFFECT AU LANCEMENT
  useEffect(() => {
    // MET EN PLACE LA LISTE DE REPONSE
    if (infiniteMode) {
      gameStore.startInfiniteGame({
        albumList: albumStore.randomOrderAlbumList,
        gameMode: gameMode,
        nbHeart: nbHeart,
        multiplicatorScore:
          parseInt(import.meta.env.VITE_POINT_MAX_HEART) -
          parseInt(import.meta.env.VITE_MALUS_PER_HEART) * (nbHeart - 1),
        endSequenceFunction: endSequence,
      });
    } else {
      gameStore.startGame({
        answerAlbumList: albumStore.getRandomAlbumList({
          nbAlbum: nbAlbumMax,
          groupId: filterAlbumMode === GROUPMODE ? groupFilterId : undefined,
        }),
        multiplicatorScore:
          (parseInt(import.meta.env.VITE_POINT_PER_ALBUM) * multiplicator) /
          (filterAlbumMode === GROUPMODE ? 2 : 1),
        endSequenceFunction: endSequence,
        gameMode: gameMode,
        groupId: groupFilterId,
        filterAlbumMode: filterAlbumMode,
      });
    }
    

    // ON SETUP L'AUDIO
    playMusic();


    // ▓▓▓ NETTOYAGE DU COMPOSANT ▓▓▓
    return () => {
      if (hintActivationTimerRef.current) clearTimeout(hintActivationTimerRef.current);
      audioObject.pause();
    };
  }, []);


  const onChangeAlbum = () => {
    if (gameStore.isFinished) return; // ou autre indicateur que le jeu est fini
    setHintActivated(false);
    playMusic();
  }

  const playMusic = () => {
    audioObject.pause();
    albumStore.getSoundUrl(gameStore.currentAlbum).then((previewData) => {
      console.log("previewData")
      console.log(previewData)
      if (previewData && previewData.url) {
        if (audioObject) {
          audioObject.setSrc(previewData.url);
          audioObject.play().catch((err) => console.warn("Erreur lecture audio :", err));
        }
        setTitleMusic(previewData.title);
      } else {
        setTitleMusic(""); // Aucun titre si pas de preview
      }
    }).catch((err) => {
      console.warn("Erreur lors de la récupération de l'extrait :", err);
      setTitleMusic("");
    });
  };

  const handleShowHint = () => {
    if (hintActivationTimerRef.current) clearTimeout(hintActivationTimerRef.current);
    if (hintActivated===false) {
      gameStore.addScore(-gameStore.multiplicatorScore/2);
    }
    setShowHint(true);
    setHintActivated(true);

    hintActivationTimerRef.current = setTimeout(() => {
      setShowHint(false);
      hintActivationTimerRef.current = null;
    }, 8000);
  };







  

  // NETTOAYAGE
  useEffect(() => {
    return () => {
      if (hintActivationTimerRef.current) clearTimeout(hintActivationTimerRef.current);
    };
  }, []);



  return (
    <section className="play_game_main">
      <div className="play_game_interfaceDiv">
        {infiniteMode ? (
          <LifeCounter heartMax={nbHeart} heart={gameStore.life} />
        ) : (
          <Timer
            timeTotal={
              parseInt(import.meta.env.VITE_TIME_PER_ALBUM) * nbAlbumMax
            }
            onFinish={
              infiniteMode
                ? () =>
                    gameStore.endSequenceInfiniteGame({ reason: "timer-end" })
                : () => gameStore.endSequence({ reason: "timer-end" })
            }
          />
        )}
        <Score
          score={gameStore.score}
          scoreMax={infiniteMode ? undefined : gameStore.scoreMax}
        />
        <div className="play_game_roundDiv">
          <h2 className="play_game_roundText">
            {infiniteMode
              ? `${gameStore.round}`
              : `${gameStore.round}/${nbAlbumMax}`}
          </h2>
        </div>
      </div>
      <p className="play_game_instruction">
        {gameMode === "date"
          ? `Quel album est sorti en premier ? (place le dans la frise)`
          : gameMode === "vente"
          ? `Quel album a fait le plus de ventes ? (place le dans la frise)`
          : ``}
      </p>
      <div className="play_game_mainDiv">
        <DndProvider backend={backend}>
          <div className="play_game_draggableDiv">
            {gameStore.currentAlbum ? (
              <DraggableZone
                album={gameStore.currentAlbum}
                group={albumStore.getGroupById(gameStore.currentAlbum.groupId)}
              />
            ) : (
              null
            )}

            <button
              title="Montrer un indice"
              type="button"
              onClick={handleShowHint}
              className="play_game_hintButton"
            >
              <Lightbulb color="#fff" size={60} />
            </button>
          </div>
          <div className="play_game_droppableDiv">
            <p className="play_game_beforeLabel">Avant</p>
            <p className="play_game_helpLabel">
              Aide toi des dates des albums déjà triés en cliquant dessus
            </p>
            <p className="play_game_afterLabel">Après</p>
            <div className="play_game_droppableList">
              <DroppableZone
                index={0}
                onDrop={
                  infiniteMode
                    ? (index) => gameStore.validateAlbumInfiniteGame({
                      index: index,
                      validateFunction: onChangeAlbum
                    })
                    : (index) => gameStore.validateAlbum({
                      index: index,
                      validateFunction: onChangeAlbum
                    })
                }
              />
              {gameStore.userAlbumList.map(({ album, correction }, i) => (
                <>
                  <AlbumDetail
                    flashingCorrection={true}
                    correction={correction}
                    album={album}
                    group={
                      gameStore?.currentAlbum
                        ? albumStore.getGroupById(album.groupId)
                        : undefined
                    }
                    showTitle={true}
                    showGroup={true}
                    showCorrectionOnFlip={true}
                  />
                  <DroppableZone
                    index={i + 1}
                    onDrop={
                      infiniteMode
                        ? (index) => gameStore.validateAlbumInfiniteGame({
                          index: index,
                          validateFunction: onChangeAlbum
                        })
                        : (index) => gameStore.validateAlbum({
                          index: index,
                          validateFunction: onChangeAlbum
                        })
                    }
                  />
                </>
              ))}
            </div>
          </div>
        </DndProvider>
      </div>
      <button
        title="Fermer la notification"
        onClick={()=>setShowHint(false)}
        className={`play_game_hintDiv ${showHint ? "show-this" : ""}`}
      >
        <p className="play_game_hintText">{gameStore?.currentAlbum?.hint}</p>
      </button>
      {audioObject && gameStore.currentAlbum && (
        <AlbumAudioPlayerSmall album={gameStore.currentAlbum} group={albumStore.getGroupById(gameStore.currentAlbum.groupId)} audioObject={audioObject} trackName={titleMusic}/>
      )}
    </section>
  );
});

export default Game;
